import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import PrevButton from '../assets/chevron_left.svg?react';
import NextButton from '../assets/chevron_right.svg?react';
import { AUTOPLAY_IMAGE_TIMER, MEDIA_PREFIX } from '../Settings';
import { u } from '../common/Units';

type PdfJs = typeof import('pdfjs-dist');
type WorkerConstructor = new () => Worker;

let pdfJsPromise: Promise<{ pdfJs: PdfJs, PdfWorker: WorkerConstructor }> | null = null;

/**
 * Load PDF.js and its worker script on first use, so they are split into their own chunks
 * and not downloaded or parsed on devices that never show a PDF.
 *
 * The legacy build (Safari 16.4+/Chrome 103+, with polyfills) is used so PDFs work on iOS 16 kiosks.
 * PDFs are not supported on iOS 14 or BrightSign. The worker is imported as a Vite worker so it is
 * transpiled to build.target, and Vite picks the right worker type (module in dev, classic in build).
 */
const loadPdfJs = () => {
  if (!pdfJsPromise) {
    pdfJsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.mjs?worker')
    ]).then(([pdfJsModule, worker]) => ({
      pdfJs: pdfJsModule as unknown as PdfJs,
      PdfWorker: worker.default as WorkerConstructor
    })).catch(e => {
      pdfJsPromise = null;
      throw e;
    });
  }
  return pdfJsPromise;
};

/** Upper limit for the canvas resolution multiplier (memory on large kiosk screens) */
const MAX_OUTPUT_SCALE = 2;

/** Minimum horizontal movement (px) for a swipe to change page */
const SWIPE_THRESHOLD = 50;

interface PdfViewerProps {
  item: any;
  close?: any;
  caption?: any;
  autoPlay?: boolean;
  autoPlayDelay?: number;
  autoPlayLoop?: boolean;
  onAutoPlayEnd?: () => void;
}

const Container = styled.div`
  position: absolute;
  /* Swipes are handled here, not by the browser or the lightbox */
  touch-action: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  display: block;
  background-color: #fff;
`;

/* Same size and look as the lightbox close button (.pswp__button--close in index.css), so they stay visible on both the page and the dark background */
const Button = styled.a`
  position: absolute;
  width: 3vw;
  height: 3vw;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  fill: #000;
  pointer-events: all;
  cursor: pointer;

  & svg {
    width: 2.2vw;
    height: 2.2vw;
  }

  /* Aligned with the close button: 2.5vw from the edge plus PhotoSwipe's 6px margin on the button */
  &.left {
    left: calc(2.5vw + 10px);
  }

  &.right {
    right: calc(2.5vw + 10px);
  }

  &:active {
    opacity: 0.8;
  }

  /* Larger touch area than the visible button */
  &&::before {
    content: '';
    position: absolute;
    padding: 50px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

/**
 * Render a PDF, one page at a time, fitted to the available space.
 *
 * - Uses PDF.js directly, loaded on demand.
 * - Supports optional autoplay page flipping.
 *
 * @returns {JSX.Element} Component template
 */
const PdfViewer: FC<PdfViewerProps> = ({
  item,
  autoPlay,
  autoPlayDelay,
  autoPlayLoop,
  onAutoPlayEnd,
}) => {
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const url = item?.src ? `${MEDIA_PREFIX}${item.src}` : null;

  // Load the document. Each viewer gets its own worker, which is terminated on cleanup.
  // A shared global worker port breaks when one document is destroyed while another loads
  // (React StrictMode, or closing one PDF and opening the next).
  useEffect(() => {
    if (!url) { return; }
    let cancelled = false;
    let webWorker: Worker | null = null;
    let pdfWorker: { destroy: () => void } | null = null;
    let loadingTask: { destroy: () => Promise<void> } | null = null;

    setPdfDocument(null);
    setPageCount(0);
    setPageNumber(1);
    loadPdfJs().then(({ pdfJs, PdfWorker }) => {
      if (cancelled) { return; }
      webWorker = new PdfWorker();
      const worker = new pdfJs.PDFWorker({ port: webWorker as any });
      pdfWorker = worker;
      // Never compile font code with eval/new Function (defence in depth, see CVE-2024-4367)
      const task = pdfJs.getDocument({ url, worker, isEvalSupported: false });
      loadingTask = task;
      return task.promise.then(doc => {
        if (cancelled) { return; }
        setPdfDocument(doc);
        setPageCount(doc.numPages);
      });
    }).catch(e => {
      if (!cancelled) { console.error("Could not load PDF", e); }
    });

    return () => {
      cancelled = true;
      // Destroying the task also destroys the loaded document
      const done = loadingTask ? loadingTask.destroy() : Promise.resolve();
      done.catch(() => {}).finally(() => {
        pdfWorker?.destroy();
        webWorker?.terminate();
      });
    };
  }, [url]);

  // Track the container size. The lightbox mounts the viewer before it has a size,
  // so window resize events are not enough.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) { return; }
    const update = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      setContainerSize(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Render the current page, fitted to the container
  useEffect(() => {
    if (!pdfDocument || !canvasRef.current || containerSize.width === 0 || containerSize.height === 0) { return; }
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    pdfDocument.getPage(pageNumber).then(page => {
      if (cancelled || !canvasRef.current) { return; }

      const unscaled = page.getViewport({ scale: 1 });
      const fit = Math.min(containerSize.width / unscaled.width, containerSize.height / unscaled.height);
      const outputScale = Math.min(window.devicePixelRatio || 1, MAX_OUTPUT_SCALE);
      const viewport = page.getViewport({ scale: fit * outputScale });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) { return; }

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(unscaled.width * fit)}px`;
      canvas.style.height = `${Math.floor(unscaled.height * fit)}px`;

      renderTask = page.render({ canvasContext: context, viewport });
      return renderTask.promise;
    }).catch(e => {
      // Cancelling a render (page change, unmount) rejects the promise
      if (!cancelled && e?.name !== "RenderingCancelledException") { console.error("Could not render PDF page", e); }
    });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdfDocument, pageNumber, containerSize]);

  /**
   * Go to previous or next page.
   *
   * @param {number} index Positive or negative index
   */
  const goToPage = useCallback(
    (index: number) => {
      const newPageNumber = pageNumber + index;

      if (newPageNumber > 0 && newPageNumber <= pageCount) {
        setPageNumber(newPageNumber);
        return;
      }

      if (autoPlay && !autoPlayLoop && onAutoPlayEnd) {
        onAutoPlayEnd();
      } else {
        setPageNumber(1);
      }
    },
    [pageCount, pageNumber, autoPlay, autoPlayLoop, onAutoPlayEnd]
  );

  // Swipe left/right to change page. Pointer events are stopped here so the lightbox
  // (PhotoSwipe) does not drag or close the slide. Not used in autoplay.
  const goToPageRef = useRef(goToPage);
  goToPageRef.current = goToPage;
  const pageRef = useRef({ pageNumber, pageCount });
  pageRef.current = { pageNumber, pageCount };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || autoPlay) { return; }
    let start: { x: number, y: number } | null = null;

    const onDown = (e: PointerEvent) => {
      e.stopPropagation();
      if (e.isPrimary) { start = { x: e.clientX, y: e.clientY }; }
    };
    const onMove = (e: PointerEvent) => { e.stopPropagation(); };
    const onUp = (e: PointerEvent) => {
      e.stopPropagation();
      if (!start || !e.isPrimary) { return; }
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      start = null;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
        // Stop at the first and last page instead of wrapping around
        const index = dx < 0 ? 1 : -1;
        const target = pageRef.current.pageNumber + index;
        if (target >= 1 && target <= pageRef.current.pageCount) {
          goToPageRef.current(index);
        }
      }
    };
    const onCancel = (e: PointerEvent) => { e.stopPropagation(); start = null; };

    container.addEventListener("pointerdown", onDown);
    container.addEventListener("pointermove", onMove);
    container.addEventListener("pointerup", onUp);
    container.addEventListener("pointercancel", onCancel);
    return () => {
      container.removeEventListener("pointerdown", onDown);
      container.removeEventListener("pointermove", onMove);
      container.removeEventListener("pointerup", onUp);
      container.removeEventListener("pointercancel", onCancel);
    };
  }, [autoPlay]);

  // Hide lightbox caption gradient
  useEffect(() => {
    const lightboxCaptionElement = document?.getElementsByClassName('pswp__default-caption')?.[0] as
      | HTMLElement
      | undefined;

    if (lightboxCaptionElement) {
      lightboxCaptionElement.style.display = 'none';
    }
  }, []);

  // Auto loop pages if autoplay is active (starts when the document has loaded)
  useEffect(() => {
    if (autoPlay && pageCount > 0) {
      const delay = autoPlayDelay || AUTOPLAY_IMAGE_TIMER;

      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }

      autoPlayRef.current = setInterval(() => {
        goToPage(1);
      }, delay);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [autoPlay, autoPlayDelay, goToPage, pageCount]);

  return (
    <Container ref={containerRef}>
      {!autoPlay && (
        <>
          {pageNumber > 1 && (
            <Button className="left" onClick={() => goToPage(-1)}>
              <PrevButton />
            </Button>
          )}
          {pageCount > pageNumber && (
            <Button className="right" onClick={() => goToPage(1)}>
              <NextButton />
            </Button>
          )}
        </>
      )}
      <Canvas ref={canvasRef} style={{ visibility: pdfDocument ? "visible" : "hidden" }}/>
    </Container>
  );
};

export default PdfViewer;
